import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DataTableConfig, DataTableFeatures } from '../components/data-table/data-table-config.interface';
import { ColumnConfig } from '../components/field-renderer/field-config.interface';
import { TableColumnsProp } from '../models/tables.model';
import { LocalStorageService } from '../services/local-storage.service';

const SYSTEM_COLUMN_KEYS = new Set(['select', 'action', 'customize']);

export interface PersistedGridColumnPreferences {
  columnOrder: string[];
  visibleKeys: string[];
}

function isSystemColumnKey(key: string): boolean {
  return SYSTEM_COLUMN_KEYS.has(key);
}

function dataKeysFromOrder(columnOrder: string[]): string[] {
  return columnOrder.filter(key => !isSystemColumnKey(key));
}

export function buildDefaultSavedColumnOrder<TRow>(
  config: DataTableConfig<TRow>
): string[] {
  const order: string[] = [];
  if (config.features.selection) {
    order.push('select');
  }
  order.push(...config.columns.map(col => String(col.key)));
  if (config.features.columnCustomization ?? true) {
    order.push('action');
  }
  return order;
}

function mergeSavedColumnOrder<TRow>(
  saved: string[],
  config: DataTableConfig<TRow>,
  defaults: string[]
): string[] {
  const configKeys = config.columns.map(col => String(col.key));
  const savedDataKeys = saved.filter(key => !isSystemColumnKey(key));
  const orderedKeys: string[] = [];

  for (const key of savedDataKeys) {
    if (configKeys.includes(key) && !orderedKeys.includes(key)) {
      orderedKeys.push(key);
    }
  }
  for (const key of configKeys) {
    if (!orderedKeys.includes(key)) {
      orderedKeys.push(key);
    }
  }

  const result: string[] = [];
  if (config.features.selection) {
    result.push('select');
  }
  result.push(...orderedKeys);
  if (config.features.columnCustomization ?? true) {
    result.push('action');
  }
  return result.length > 1 ? result : defaults;
}

function buildDefaultPreferences<TRow>(
  config: DataTableConfig<TRow>
): PersistedGridColumnPreferences {
  const columnOrder = buildDefaultSavedColumnOrder(config);
  const visibleKeys = config.columns
    .filter(col => col.isSelected !== false)
    .map(col => String(col.key));
  return { columnOrder, visibleKeys };
}

function parseLegacySavedPreferences<TRow>(
  saved: string[],
  config: DataTableConfig<TRow>
): PersistedGridColumnPreferences {
  const allConfigKeys = config.columns.map(col => String(col.key));
  const rawSavedDataKeys = dataKeysFromOrder(saved).filter(key =>
    allConfigKeys.includes(key)
  );
  const columnOrder = mergeSavedColumnOrder(
    saved,
    config,
    buildDefaultSavedColumnOrder(config)
  );

  if (rawSavedDataKeys.length < allConfigKeys.length) {
    return { columnOrder, visibleKeys: rawSavedDataKeys };
  }

  const hiddenByDefault = new Set(
    config.columns
      .filter(col => col.isSelected === false)
      .map(col => String(col.key))
  );
  let trailingHiddenStart = rawSavedDataKeys.length;
  for (let i = rawSavedDataKeys.length - 1; i >= 0; i--) {
    if (hiddenByDefault.has(rawSavedDataKeys[i])) {
      trailingHiddenStart = i;
    } else {
      break;
    }
  }
  const visibleKeys = rawSavedDataKeys.slice(0, trailingHiddenStart);
  const defaultVisibleKeys = config.columns
    .filter(col => col.isSelected !== false)
    .map(col => String(col.key));
  return {
    columnOrder,
    visibleKeys: visibleKeys.length > 0 ? visibleKeys : defaultVisibleKeys,
  };
}

export function resolvePrintDisplayColumns(
  dataTable: { getPrintDisplayColumns(): string[] } | null | undefined,
  fallbackColumns: string[]
): string[] {
  return dataTable?.getPrintDisplayColumns() ?? structuredClone(fallbackColumns);
}

function mergeMissingDefaultColumns(
  stored: string[],
  fallback: string[]
): string[] {
  const missing = fallback.filter(column => !stored.includes(column));
  if (!missing.length) {
    return stored;
  }
  const errorIndex = stored.indexOf('error');
  const insertAt = errorIndex >= 0 ? errorIndex : stored.length;
  return [...stored.slice(0, insertAt), ...missing, ...stored.slice(insertAt)];
}

/** Normalize raw localStorage value to flat mat-header column order. */
export function readStoredColumnOrder(
  raw: unknown,
  fallback: string[]
): string[] {
  let stored: string[] | null = null;
  if (
    raw &&
    typeof raw === 'object' &&
    !Array.isArray(raw) &&
    Array.isArray((raw as PersistedGridColumnPreferences).columnOrder)
  ) {
    stored = (raw as PersistedGridColumnPreferences).columnOrder;
  } else if (Array.isArray(raw) && raw.length > 0) {
    stored = raw;
  }
  if (!stored) {
    return fallback;
  }
  return mergeMissingDefaultColumns(stored, fallback);
}

export function loadGridColumnPreferences<TRow>(
  storage: LocalStorageService,
  storageKey: string,
  config: DataTableConfig<TRow>
): PersistedGridColumnPreferences {
  const defaults = buildDefaultPreferences(config);
  const saved = storage.getItem(storageKey);

  if (!saved) {
    return defaults;
  }

  if (
    typeof saved === 'object' &&
    !Array.isArray(saved) &&
    Array.isArray(saved.columnOrder) &&
    Array.isArray(saved.visibleKeys)
  ) {
    return {
      columnOrder: mergeSavedColumnOrder(saved.columnOrder, config, defaults.columnOrder),
      visibleKeys: saved.visibleKeys.filter((key: string) =>
        config.columns.some(col => String(col.key) === key)
      ),
    };
  }

  if (Array.isArray(saved) && saved.length > 0) {
    return parseLegacySavedPreferences(saved, config);
  }

  return defaults;
}

export function loadSavedColumnOrder<TRow>(
  storage: LocalStorageService,
  storageKey: string,
  config: DataTableConfig<TRow>
): string[] {
  return loadGridColumnPreferences(storage, storageKey, config).columnOrder;
}

export function buildTableColumnPropsFromConfig<TRow>(
  config: DataTableConfig<TRow>
): TableColumnsProp[] {
  return config.columns.map(col => ({
    key: String(col.key),
    label: col.label,
    isSelected: col.isSelected !== false,
    defaultView: col.defaultView ?? true,
  }));
}

export function applySavedColumnOrder<TRow>(
  config: DataTableConfig<TRow>,
  preferences: PersistedGridColumnPreferences
): {
  tableColumnProps: TableColumnsProp[];
  visibleColumns: ColumnConfig<TRow>[];
} {
  const dataKeyOrder = dataKeysFromOrder(preferences.columnOrder);
  const visibleKeySet = new Set(preferences.visibleKeys);
  const propsByKey = new Map(
    buildTableColumnPropsFromConfig(config).map(prop => [prop.key, { ...prop }])
  );
  const tableColumnProps: TableColumnsProp[] = [];

  for (const key of dataKeyOrder) {
    const prop = propsByKey.get(key);
    if (prop) {
      prop.isSelected = visibleKeySet.has(key);
      tableColumnProps.push(prop);
      propsByKey.delete(key);
    }
  }
  propsByKey.forEach(prop => {
    prop.isSelected = visibleKeySet.has(prop.key);
    tableColumnProps.push(prop);
  });

  const visibleColumns = preferences.visibleKeys
    .map(key => config.columns.find(col => String(col.key) === key))
    .filter((col): col is ColumnConfig<TRow> => !!col);

  return { tableColumnProps, visibleColumns };
}

export function saveGridColumnPreferences(
  storage: LocalStorageService,
  storageKey: string,
  tableColumnProps: TableColumnsProp[],
  visibleColumnKeys: string[],
  features: DataTableFeatures
): void {
  const columnOrder: string[] = [];
  if (features.selection) {
    columnOrder.push('select');
  }
  columnOrder.push(...tableColumnProps.map(prop => prop.key));
  if (features.columnCustomization ?? true) {
    columnOrder.push('action');
  }
  const preferences: PersistedGridColumnPreferences = {
    columnOrder,
    visibleKeys: visibleColumnKeys,
  };
  storage.setItem(storageKey, preferences);
}

export function saveColumnOrder(
  storage: LocalStorageService,
  storageKey: string,
  tableColumnProps: TableColumnsProp[],
  features: DataTableFeatures
): void {
  const visibleKeys = tableColumnProps
    .filter(prop => prop.isSelected)
    .map(prop => prop.key);
  saveGridColumnPreferences(
    storage,
    storageKey,
    tableColumnProps,
    visibleKeys,
    features
  );
}

/** Customize menu order: checked columns in grid order, unchecked ones at their config slot (so toggling never moves an item). */
export function sortTableColumnPropsByVisibleOrder<TRow>(
  tableColumnProps: TableColumnsProp[],
  visibleColumnKeys: string[],
  config: DataTableConfig<TRow>
): TableColumnsProp[] {
  const propsByKey = new Map(tableColumnProps.map(prop => [prop.key, prop]));
  return placeAtConfigSlot(visibleColumnKeys, [...propsByKey.keys()], config)
    .map(key => propsByKey.get(key))
    .filter((prop): prop is TableColumnsProp => !!prop);
}

/**
 * Orders `keys`: those already in `orderedKeys` keep that order; the rest go to their config slot,
 * right after the nearest earlier config column already placed.
 */
export function placeAtConfigSlot<TRow>(
  orderedKeys: string[],
  keys: string[],
  config: DataTableConfig<TRow>
): string[] {
  const keySet = new Set(keys);
  const result = orderedKeys.filter(key => keySet.has(key));
  const toPlace = new Set(keys.filter(key => !orderedKeys.includes(key)));
  let insertAt = 0;
  for (const col of config.columns) {
    const key = String(col.key);
    if (toPlace.delete(key)) {
      result.splice(insertAt++, 0, key);
    } else if (result.includes(key)) {
      insertAt = result.indexOf(key) + 1;
    }
  }
  return [...result, ...toPlace];
}

export function reorderColumnsAfterHeaderDrop<TRow>(
  event: CdkDragDrop<string[]>,
  visibleColumns: ColumnConfig<TRow>[],
  tableColumnProps: TableColumnsProp[],
  config: DataTableConfig<TRow>
): { visibleColumns: ColumnConfig<TRow>[]; tableColumnProps: TableColumnsProp[] } {
  const reorderedVisible = [...visibleColumns];
  moveItemInArray(reorderedVisible, event.previousIndex, event.currentIndex);
  const visibleKeys = reorderedVisible.map(col => String(col.key));
  const reorderedProps = sortTableColumnPropsByVisibleOrder(
    tableColumnProps,
    visibleKeys,
    config
  );
  return {
    visibleColumns: reorderedVisible,
    tableColumnProps: reorderedProps,
  };
}

/**
 * Filters download column headers and keys based on user's column visibility preferences.
 * 
 * Respects user customization while preserving default behavior when no customization exists.
 * System columns ('select', 'action', 'customize') are always excluded from export.
 * 
 * @param storage - LocalStorage service instance
 * @param storageKey - Storage key for the grid's column preferences
 * @param allHeaders - Complete array of column header labels for display
 * @param allColumnKeys - Complete array of column keys matching header positions
 * @returns Object containing filtered headers and columnKeys arrays
 * 
 * Preservation behavior (returns all columns unchanged):
 * - No preferences exist in LocalStorage
 * - visibleKeys is empty
 * - All data columns are visible (visibleKeys length equals all non-system columns)
 * 
 * Bug fix behavior (filters to visible only):
 * - User has customized column visibility
 * - visibleKeys contains subset of available columns
 * - Hidden columns are excluded from both headers and columnKeys
 */
export function filterDownloadColumnsByVisibility(
  storage: LocalStorageService,
  storageKey: string,
  allHeaders: string[],
  allColumnKeys: string[]
): { headers: string[]; columnKeys: string[] } {
  const saved = storage.getItem(storageKey);

  // Preservation: no preferences exist
  if (!saved) {
    return { headers: allHeaders, columnKeys: allColumnKeys };
  }

  // Extract visibleKeys from preferences
  let visibleKeys: string[] = [];
  if (
    typeof saved === 'object' &&
    !Array.isArray(saved) &&
    Array.isArray((saved as PersistedGridColumnPreferences).visibleKeys)
  ) {
    visibleKeys = (saved as PersistedGridColumnPreferences).visibleKeys;
  }

  // Preservation: empty visibleKeys
  if (visibleKeys.length === 0) {
    return { headers: allHeaders, columnKeys: allColumnKeys };
  }

  // Count non-system columns in allColumnKeys
  const nonSystemColumnKeys = allColumnKeys.filter(key => !isSystemColumnKey(key));

  // Preservation: all data columns are visible
  if (visibleKeys.length >= nonSystemColumnKeys.length) {
    return { headers: allHeaders, columnKeys: allColumnKeys };
  }

  // Bug fix: filter to visible columns only
  const visibleKeySet = new Set(visibleKeys);
  const filteredHeaders: string[] = [];
  const filteredColumnKeys: string[] = [];

  for (let i = 0; i < allColumnKeys.length; i++) {
    const key = allColumnKeys[i];
    // Exclude system columns and hidden columns
    if (!isSystemColumnKey(key) && visibleKeySet.has(key)) {
      filteredHeaders.push(allHeaders[i]);
      filteredColumnKeys.push(key);
    }
  }

  return { headers: filteredHeaders, columnKeys: filteredColumnKeys };
}
